import React from "react";
import {
  SCROLL_OFFSET,
  ARTICLE_COLUMN,
  H2,
  BODY,
  BODY_SM,
  CARD_TITLE,
  STACK_GAP,
} from "./sections";

const PRINCIPLES = [
  {
    title: "Give context before the task",
    body: 'Say who you are, what you are working on, or what the output is for, before you state the actual request. "I am writing a blog post for a small business audience, and I need..." gives the model far more to work with than starting cold with the task itself.',
  },
  {
    title: "Be specific about the format you want back",
    body: 'Say explicitly whether you want a list, a short paragraph, a table, a certain word count, or a particular tone. Voice makes it easy to tack this on naturally: "...and give it to me as a bulleted list, not a full paragraph."',
  },
  {
    title: "Include examples when you have them",
    body: 'If you have an example of the tone, style, or format you want matched, describe it or reference it directly. "Write it in the same style as the last one you gave me" or "keep the tone like a casual email, not a formal report" both work well spoken.',
  },
  {
    title: "State constraints clearly",
    body: "Mention anything the AI should avoid or stay within: length limits, topics to skip, information it should not assume. These are easy to forget when typing a short prompt but come out naturally when you talk through a request out loud.",
  },
  {
    title: "Break multi-part requests into clear steps",
    body: 'If your prompt has more than one part — for example "summarize this, then rewrite the summary for a general audience" — say each part as a distinct step. It mirrors how people naturally give multi-step instructions in conversation.',
  },
];

const StrongPromptSection = () => {
  return (
    <section
      id="strong-prompt"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      <h2 className={H2}>How To Write A Strong Prompt, Adapted For Voice</h2>

      <p className={`${STACK_GAP} ${BODY}`}>
        The core principles of good prompt writing do not change when you switch
        from typing to talking. What changes is how naturally you can hit them,
        since speaking makes it easier to include detail without the request
        feeling tedious to compose.
      </p>

      {/* Numbered list — each principle separated by a hairline rule */}
      <div className="pt-[30.05px]">
        <ol className="flex flex-col">
          {PRINCIPLES.map(({ title, body }, index) => (
            <li
              key={title}
              className={`flex gap-[17.17px] border-t-[1.07px] border-[#E6ECF5] pt-[21.46px] ${
                index === PRINCIPLES.length - 1 ? "pb-0" : "pb-[21.46px]"
              }`}
            >
              <span className="w-[16px] shrink-0 font-jakarta text-[16.1px] font-bold leading-[26.16px] tracking-[0px] text-[#3AB1F6]">
                {index + 1}
              </span>

              <div className="min-w-0">
                <h3 className={CARD_TITLE}>{title}</h3>
                <p className={`mt-[4px] ${BODY_SM}`}>{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default StrongPromptSection;
